import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ArrowLeftIcon } from 'lucide-react';
type Location = {
  name: string;
  subtitle?: string;
  address: string;
  googleUrl: string;
  facebookUrl: string;
  yelpUrl: string;
};
const locations: Location[] = [{
  name: 'Mitchellville',
  subtitle: 'Main Office',
  address: '10,240 Lake Arbor Way, Mitchellville, MD 20721',
  googleUrl: 'https://search.google.com/local/writereview?placeid=PLACEHOLDER_mitchellville',
  facebookUrl: 'https://facebook.com/visualeyesgroupmitchellville/reviews',
  yelpUrl: 'https://yelp.com/biz/visual-eyes-mitchellville-md'
}, {
  name: 'Clinton',
  address: '8,931 Woodyard Rd, Clinton, MD 20735',
  googleUrl: 'https://search.google.com/local/writereview?placeid=PLACEHOLDER_clinton',
  facebookUrl: 'https://facebook.com/visualeyesgroupclinton/reviews',
  yelpUrl: 'https://yelp.com/biz/visual-eyes-clinton-md'
}, {
  name: 'Hyattsville',
  address: '1,835 University Blvd. Ste 228, Hyattsville, MD 20783',
  googleUrl: 'https://search.google.com/local/writereview?placeid=PLACEHOLDER_hyattsville',
  facebookUrl: 'https://facebook.com/visualeyesgrouphyattsville/reviews',
  yelpUrl: 'https://yelp.com/biz/visual-eyes-hyattsville-md'
}, {
  name: 'Greenbelt',
  address: '6,088 Greenbelt Road Unit 44, Greenbelt, MD 20770',
  googleUrl: 'https://search.google.com/local/writereview?placeid=PLACEHOLDER_greenbelt',
  facebookUrl: 'https://facebook.com/visualeyesgroupgreenbelt/reviews',
  yelpUrl: 'https://yelp.com/biz/visual-eyes-greenbelt-md'
}, {
  name: 'Silver Spring',
  address: '8,121 Georgia Ave, Ste 800, Silver Spring, MD 20910',
  googleUrl: 'https://search.google.com/local/writereview?placeid=PLACEHOLDER_silverspring',
  facebookUrl: 'https://facebook.com/visualeyesgroupsilverspring/reviews',
  yelpUrl: 'https://yelp.com/biz/visual-eyes-silverspring-md'
}, {
  name: 'Frederick',
  address: '61 Thomas Johnson Dr, Ste 201, Frederick, MD 21702',
  googleUrl: 'https://search.google.com/local/writereview?placeid=PLACEHOLDER_frederick',
  facebookUrl: 'https://facebook.com/visualeyesgroupfrederick/reviews',
  yelpUrl: 'https://yelp.com/biz/visual-eyes-frederick-md'
}];
export function ReviewsSection() {
  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState<'location' | 'platform'>('location');
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  // Handle escape key to close modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showModal) {
        closeModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showModal]);
  const openModal = () => {
    setStep('location');
    setSelectedLocation(null);
    setShowModal(true);
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
  };
  const closeModal = () => {
    setShowModal(false);
    setTimeout(() => {
      setStep('location');
      setSelectedLocation(null);
    }, 300); // Reset after animation
    document.body.style.overflow = 'unset';
  };
  const handleLocationSelect = (location: Location) => {
    setSelectedLocation(location);
    setStep('platform');
  };
  const handleBack = () => {
    setStep('location');
    setSelectedLocation(null);
  };
  const stepVariants = {
    initial: (direction: number) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0
    }),
    animate: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: 'easeOut'
      }
    },
    exit: (direction: number) => ({
      x: direction > 0 ? -50 : 50,
      opacity: 0,
      transition: {
        duration: 0.2,
        ease: 'easeIn'
      }
    })
  };
  // Direction for animation: 1 for moving forward (location -> platform), -1 for moving backward
  const direction = step === 'platform' ? 1 : -1;
  return <>
      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-surface-muted relative z-10" data-id="element-463">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-464">
          <div className="text-center max-w-2xl mx-auto flex flex-col items-center" data-id="element-465">
            <motion.span initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="inline-block text-brand-gold uppercase tracking-wider text-sm font-bold mb-3" data-id="element-466">
              Share Your Experience
            </motion.span>
            <motion.h2 initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-2xl md:text-3xl font-heading font-bold text-brand-dark mb-4" data-id="element-467">
              Leave Us a Review
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="text-neutral-text/70 mb-8" data-id="element-468">
              Your feedback helps us improve and helps others find quality eye
              care.
            </motion.p>
            <motion.button initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} onClick={openModal} className="bg-brand-dark text-white hover:bg-brand-gold px-8 py-4 rounded-button font-heading font-semibold transition-colors focus-ring shadow-md hover:shadow-lg" data-id="element-469">
              Write a Review
            </motion.button>
          </div>
        </div>
      </section>

      {/* Review Flow Modal */}
      <AnimatePresence data-id="element-470">
        {showModal && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-id="element-471">
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="absolute inset-0 bg-brand-dark/60 backdrop-blur-md" onClick={closeModal} data-id="element-472" />

            {/* Modal Container */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} className="relative z-10 bg-white rounded-card shadow-2xl w-full max-w-lg p-6 md:p-8 overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-473">
              {/* Close Button */}
              <button onClick={closeModal} className="absolute top-4 right-4 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100/80 hover:bg-gray-200 text-neutral-text/70 hover:text-neutral-text transition-colors focus-ring" aria-label="Close review modal" data-id="element-474">
                <XIcon className="w-5 h-5" data-id="element-475" />
              </button>

              <div className="relative min-h-[350px]" data-id="element-476">
                <AnimatePresence mode="wait" custom={direction} data-id="element-477">
                  {step === 'location' && <motion.div key="location-step" custom={direction} variants={stepVariants} initial="initial" animate="animate" exit="exit" className="w-full" data-id="element-478">
                      <h3 className="text-xl font-heading font-bold text-brand-dark text-center mb-6 pr-8 pl-8" data-id="element-479">
                        Select Your Location
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-480">
                        {locations.map(location => <button key={location.name} onClick={() => handleLocationSelect(location)} className="bg-surface-muted hover:bg-brand-gold/10 hover:border-brand-gold/30 border border-gray-100 rounded-card p-4 text-left cursor-pointer transition-all duration-200 focus-ring group" data-id="element-481">
                            <div className="flex items-center gap-2 mb-1" data-id="element-482">
                              <span className="text-sm font-heading font-bold text-brand-dark group-hover:text-brand-gold transition-colors" data-id="element-483">
                                {location.name}
                              </span>
                              {location.subtitle && <span className="bg-brand-gold/10 text-brand-gold text-[9px] uppercase font-bold px-1.5 py-0.5 rounded-sm tracking-wider" data-id="element-484">
                                  {location.subtitle}
                                </span>}
                            </div>
                            <p className="text-xs text-neutral-text/60 line-clamp-2" data-id="element-485">
                              {location.address}
                            </p>
                          </button>)}
                      </div>
                    </motion.div>}

                  {step === 'platform' && selectedLocation && <motion.div key="platform-step" custom={direction} variants={stepVariants} initial="initial" animate="animate" exit="exit" className="w-full flex flex-col h-full" data-id="element-486">
                      {/* Back Button */}
                      <button onClick={handleBack} className="absolute -top-2 -left-2 p-2 flex items-center gap-1 text-sm text-neutral-text/60 hover:text-brand-dark transition-colors focus-ring rounded-md z-20" aria-label="Go back to location selection" data-id="element-487">
                        <ArrowLeftIcon className="w-4 h-4" data-id="element-488" />
                        <span data-id="element-489">Back</span>
                      </button>

                      <div className="mt-8 mb-8 text-center" data-id="element-490">
                        <h3 className="text-xl font-heading font-bold text-brand-dark mb-2" data-id="element-491">
                          Review {selectedLocation.name}
                        </h3>
                        <p className="text-sm text-neutral-text/60 px-4" data-id="element-492">
                          {selectedLocation.address}
                        </p>
                      </div>

                      <div className="space-y-3 mt-auto" data-id="element-493">
                        <a href={selectedLocation.googleUrl} target="_blank" rel="noreferrer" className="block w-full text-center bg-brand-gold hover:bg-brand-gold-light text-white py-4 rounded-button font-heading font-semibold text-base transition-colors focus-ring shadow-sm hover:shadow-md" aria-label={`Review ${selectedLocation.name} on Google`} onClick={closeModal} data-id="element-494">
                          Google
                        </a>
                        <a href={selectedLocation.facebookUrl} target="_blank" rel="noreferrer" className="block w-full text-center bg-brand-gold hover:bg-brand-gold-light text-white py-4 rounded-button font-heading font-semibold text-base transition-colors focus-ring shadow-sm hover:shadow-md" aria-label={`Review ${selectedLocation.name} on Facebook`} onClick={closeModal} data-id="element-495">
                          Facebook
                        </a>
                        <a href={selectedLocation.yelpUrl} target="_blank" rel="noreferrer" className="block w-full text-center bg-brand-gold hover:bg-brand-gold-light text-white py-4 rounded-button font-heading font-semibold text-base transition-colors focus-ring shadow-sm hover:shadow-md" aria-label={`Review ${selectedLocation.name} on Yelp`} onClick={closeModal} data-id="element-496">
                          Yelp
                        </a>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </>;
}